import { Autocomplete, Checkbox, Chip, FormControl, FormControlLabel, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { ContentInventory } from '../../hooks/useInventory';
import { countActiveVersionsMatchingTags, distinctInventoryTags } from './inventoryTemplateSelection';

type Props = {
    inventory: ContentInventory[] | undefined;
    loading?: boolean;
    value: string[];
    onChange: (nextTags: string[]) => void;
    /** Only include templates with a price in the pool, so free content is not sent by accident */
    paidOnly: boolean;
    onPaidOnlyChange: (paidOnly: boolean) => void;
    error?: string;
};

/**
 * Strict multi-select of template tags - only tags that currently exist on ACTIVE template
 * variations are offered. The matching template pool is resolved dynamically at send/post
 * time, so the live match count shown here is a preview, not a snapshot.
 */
const InventoryTemplateTagsSelector = (props: Props) => {
    const { inventory, loading = false, value, onChange, paidOnly, onPaidOnlyChange, error } = props;

    const options = useMemo(() => distinctInventoryTags(inventory || []), [inventory]);

    const matchCountByTag = useMemo(() => {
        const counts = new Map<string, number>();
        options.forEach(tag => {
            counts.set(tag, countActiveVersionsMatchingTags(inventory || [], [tag], paidOnly));
        });
        return counts;
    }, [inventory, options, paidOnly]);

    const totalMatches = useMemo(
        () => countActiveVersionsMatchingTags(inventory || [], value, paidOnly),
        [inventory, value, paidOnly],
    );

    if (loading) {
        return <LinearProgress />;
    }

    return (
        <FormControl fullWidth>
            <Autocomplete
                multiple
                size="small"
                options={options}
                value={value}
                onChange={(_, nextValue) => onChange(nextValue)}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => <Chip {...getTagProps({ index })} key={option} label={option} size="small" />)
                }
                renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ minWidth: 0, mr: 1 }}>
                                {option}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {(matchCountByTag.get(option) || 0).toLocaleString()} template
                                {(matchCountByTag.get(option) || 0) === 1 ? '' : 's'}
                            </Typography>
                        </Stack>
                    </li>
                )}
                renderInput={params => (
                    <TextField {...params} placeholder={value.length === 0 ? 'Select template tags...' : ''} error={!!error} />
                )}
                noOptionsText="No tags found on active templates"
            />
            <FormControlLabel
                control={<Checkbox size="small" checked={paidOnly} onChange={e => onPaidOnlyChange(e.target.checked)} />}
                label={
                    <Typography variant="body2">
                        Only include paid templates <em>(recommended - avoids sending free content by accident)</em>
                    </Typography>
                }
                sx={{ mt: 0.5 }}
            />
            <Typography
                variant="caption"
                color={value.length > 0 && totalMatches === 0 ? 'warning.main' : 'text.secondary'}
                sx={{ mt: 0.5, display: 'block' }}
            >
                {value.length === 0
                    ? 'A random template carrying any of the selected tags will be picked each time'
                    : `${totalMatches.toLocaleString()} active ${paidOnly ? 'paid ' : ''}template${
                          totalMatches === 1 ? '' : 's'
                      } currently match the selected tag(s)`}
            </Typography>
            {error && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {error}
                </Typography>
            )}
        </FormControl>
    );
};

export default InventoryTemplateTagsSelector;
